/**
 * Active ad account resolution — matches `sourceMedium` (init override or
 * page UTMs) against website `config.adAccounts`.
 *
 * Kernel-level so ecom and pixels share one match.
 */

import type { AdAccountConfig } from '../config/types';
import type { PageParams } from '../analytics/pageParams';

export interface UtmParams {
  source?: string;
  medium?: string;
  campaign?: string;
  mbid?: string;
}

export function utmFromPageParams(pageParams: PageParams): UtmParams {
  return {
    source: pageParams.utmSource || undefined,
    medium: pageParams.utmMedium || undefined,
    campaign: pageParams.utmCampaign || undefined,
    mbid: pageParams.mediaBuyingId || undefined,
  };
}

/**
 * Resolve the `source/medium` match key.
 * Precedence: `init({ sourceMedium })` > page `utm_source`/`utm_medium`.
 */
export function resolveSourceMedium(opts: {
  initSourceMedium?: string;
  pageParams: PageParams;
}): string | null {
  const fromInit = opts.initSourceMedium?.trim().toLowerCase();
  if (fromInit) {
    if (fromInit.includes('/')) return fromInit;
    return null;
  }
  const utm = utmFromPageParams(opts.pageParams);
  if (utm.source && utm.medium) {
    return `${utm.source}/${utm.medium}`.toLowerCase();
  }
  return null;
}

/**
 * Split the matched account's first `source/medium` pair.
 * Platform is not the UTM source — accounts often use aliases like `fb1/paid`.
 */
export function sourceMediumPartsFromAccount(
  account: Pick<AdAccountConfig, 'sourceMedium'>,
): { source: string; medium: string } {
  const [source = '', medium = ''] = account.sourceMedium[0]?.split('/') ?? [];
  return { source, medium };
}

/** First ad account whose `sourceMedium` includes the key, or null. */
export function resolveActiveAdAccount(
  adAccounts: AdAccountConfig[],
  sourceMedium: string | null,
): AdAccountConfig | null {
  if (!sourceMedium) return null;

  for (const account of adAccounts) {
    if (account.sourceMedium.includes(sourceMedium)) {
      return account;
    }
  }
  return null;
}
